import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import FormWithValidation from "../../components/common/FormWithValidation";
import Loader from "../../components/common/Loader";
import { createFramework, getFramework, updateFramework } from "../../api/frameworks";
import { useToast } from "../../components/common/ToastProvider";
import Input from "../../ui/Input";
import Textarea from "../../ui/Textarea";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  code: z.string().regex(/^[A-Z0-9._-]+$/, "Code must match pattern A-Z0-9._-"),
  description: z.string().optional().nullable(),
});

export default function FrameworkForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [serverFieldErrors, setServerFieldErrors] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["framework", id],
    queryFn: () => getFramework(id),
    enabled: isEdit,
  });

  const mutation = useMutation({
    mutationFn: (payload) => (isEdit ? updateFramework(id, payload) : createFramework(payload)),
    onSuccess: (result) => {
      success(isEdit ? "Framework updated" : "Framework created");
      queryClient.invalidateQueries({ queryKey: ["frameworks"] });
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ["framework", id] });
      }
      navigate(`/frameworks/${result.id}`);
    },
    onError: (err) => {
      setServerFieldErrors(err?.fieldErrors || {});
      showError(err?.detail || "Failed to save framework");
    },
  });

  const defaultValues = useMemo(
    () => ({
      name: data?.name || "",
      code: data?.code || "",
      description: data?.description || "",
    }),
    [data]
  );

  const breadcrumbItems = [
    { label: "Frameworks", to: "/frameworks" },
    { label: isEdit ? "Edit Framework" : "New Framework" },
  ];

  return (
    <div>
      <Breadcrumbs items={breadcrumbItems} />
      <h1>{isEdit ? "Edit Framework" : "New Framework"}</h1>
      {isEdit && isLoading && <Loader />}
      <FormWithValidation
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={(values) => mutation.mutate(values)}
        onCancel={() => navigate(isEdit ? `/frameworks/${id}` : "/frameworks")}
        serverFieldErrors={serverFieldErrors}
        submitLabel={isEdit ? "Save" : "Create"}
      >
        {({ register, formState }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "400px" }}>
            <label>
              Name
              <Input data-testid="framework-name" error={formState.errors.name?.message} {...register("name")} />
            </label>
            <label>
              Code
              <Input data-testid="framework-code" error={formState.errors.code?.message} {...register("code")} />
            </label>
            <label>
              Description
              <Textarea data-testid="framework-description" error={formState.errors.description?.message} {...register("description")} />
            </label>
          </div>
        )}
      </FormWithValidation>
    </div>
  );
}
